import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function AvisoPrivacidadPage() {
  return (
    <div className="min-h-screen bg-white text-zinc-900" data-testid="privacidad-page">
      <header className="border-b border-zinc-200 bg-white">
        <div className="max-w-3xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-zinc-600 hover:text-zinc-900 transition-colors"
            data-testid="privacidad-back-home"
          >
            <ArrowLeft className="w-4 h-4" /> Volver al inicio
          </Link>
          <Link to="/" className="text-lg font-bold tracking-tight text-zinc-900">
            Dealider
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12 leading-relaxed text-[15px]">
        <h1 className="text-3xl sm:text-4xl font-bold text-zinc-900 mb-2">Aviso de Privacidad</h1>
        <p className="text-zinc-600 mb-1">
          Dealider · Plataforma de Gestión de Relaciones con Clientes (CRM)
        </p>
        <p className="text-zinc-600 mb-1">app.dealider.com · Aplicaciones Móviles iOS y Android</p>
        <p className="text-sm text-zinc-500 mb-10">Última actualización: 21 de mayo de 2026</p>

        <Section n="I" title="Identidad y Domicilio del Responsable">
          <p>
            <strong>Dragon Technologies SAPI de CV</strong>
            <br />
            RFC: DTE220909DT1
            <br />
            Domicilio: Río Nazas 42, Cuauhtémoc, Ciudad de México, CP 06500, México
            <br />
            Web:{" "}
            <a
              href="https://app.dealider.com"
              className="text-indigo-600 hover:underline"
              target="_blank"
              rel="noreferrer"
            >
              https://app.dealider.com
            </a>
          </p>
        </Section>

        <Section n="II" title="Datos Personales que Recabamos">
          <h3 className="text-lg font-semibold text-zinc-900 mt-4 mb-2">
            (A) Datos de Usuarios de Dealider
          </h3>
          <ul className="list-disc pl-6 space-y-1">
            <li>Identificación y contacto: nombre, correo electrónico, teléfono.</li>
            <li>Autenticación: credenciales, tokens, registros de inicio de sesión.</li>
            <li>Fiscales y facturación: RFC, razón social, domicilio fiscal, método de pago.</li>
            <li>
              Uso y técnicos: dirección IP, dispositivo, sistema operativo, navegador, métricas de
              uso de la Plataforma.
            </li>
            <li>Comunicación: contenido de tickets de soporte y comunicaciones con Dealider.</li>
          </ul>
          <h3 className="text-lg font-semibold text-zinc-900 mt-4 mb-2">
            (B) Datos de Clientes/Prospectos gestionados por Usuarios
          </h3>
          <p>
            Dealider actúa como <strong>Encargado</strong> del tratamiento de los datos personales
            que los Usuarios cargan o generan en la Plataforma respecto de sus propios clientes y
            prospectos. El Usuario es el Responsable de dichos datos frente a sus titulares.
          </p>
        </Section>

        <Section n="III" title="Finalidades del Tratamiento">
          <h3 className="text-lg font-semibold text-zinc-900 mt-4 mb-2">(A) Finalidades Primarias</h3>
          <ul className="list-disc pl-6 space-y-1">
            <li>Creación y mantenimiento de la cuenta de Usuario.</li>
            <li>Operación del CRM y de los módulos contratados.</li>
            <li>Procesamiento de pagos y facturación.</li>
            <li>Atención y soporte al Usuario.</li>
            <li>Seguridad, prevención de fraudes y auditoría.</li>
            <li>Cumplimiento de obligaciones legales y regulatorias.</li>
          </ul>
          <h3 className="text-lg font-semibold text-zinc-900 mt-4 mb-2">
            (B) Finalidades Secundarias (requieren consentimiento)
          </h3>
          <ul className="list-disc pl-6 space-y-1">
            <li>Marketing directo y envío de comunicaciones promocionales.</li>
            <li>Análisis de uso y estadísticas agregadas para mejora del servicio.</li>
            <li>Encuestas de satisfacción y estudios de mercado.</li>
          </ul>
          <p className="mt-3">
            El Usuario puede oponerse al tratamiento para finalidades secundarias en cualquier
            momento escribiendo a{" "}
            <a href="mailto:privacidad@dealider.com" className="text-indigo-600 hover:underline">
              privacidad@dealider.com
            </a>
            .
          </p>
        </Section>

        <Section n="IV" title="Base Legal para el Tratamiento">
          <ul className="list-disc pl-6 space-y-1">
            <li>Consentimiento del titular.</li>
            <li>Ejecución del contrato de prestación de servicios.</li>
            <li>Cumplimiento de obligaciones legales aplicables.</li>
            <li>Interés legítimo de Dealider para garantizar la seguridad y mejora del servicio.</li>
          </ul>
        </Section>

        <Section n="V" title="Transferencias de Datos Personales">
          <p>Los datos personales podrán transferirse a:</p>
          <ul className="list-disc pl-6 space-y-1 mt-2">
            <li>Proveedores de infraestructura tecnológica (AWS, Google Cloud Platform).</li>
            <li>Procesadores de pago (Stripe, Conekta, PayPal, entre otros).</li>
            <li>Servicios de envío de correo y notificaciones.</li>
            <li>Herramientas de análisis, monitoreo y telemetría.</li>
            <li>Autoridades competentes cuando exista requerimiento fundado.</li>
            <li>
              Terceros en el marco de fusiones, adquisiciones o reorganizaciones corporativas,
              previa notificación al titular.
            </li>
          </ul>
        </Section>

        <Section n="VI" title="Medidas de Seguridad">
          <p>
            Dealider implementa medidas administrativas, técnicas y físicas razonables para
            proteger los datos personales contra pérdida, alteración, acceso no autorizado o uso
            indebido, incluyendo:
          </p>
          <ul className="list-disc pl-6 space-y-1 mt-2">
            <li>Cifrado TLS/SSL en tránsito y AES-256 en reposo.</li>
            <li>Controles de acceso basados en roles (RBAC).</li>
            <li>Autenticación de dos factores (2FA/MFA) para cuentas privilegiadas.</li>
            <li>Monitoreo continuo y registro de actividad.</li>
            <li>Pruebas periódicas de penetración (penetration testing).</li>
            <li>Respaldos cifrados de información crítica.</li>
          </ul>
        </Section>

        <Section n="VII" title="Derechos de los Titulares (ARCO y Derechos Adicionales)">
          <p>El titular puede ejercer los siguientes derechos:</p>
          <ul className="list-disc pl-6 space-y-1 mt-2">
            <li>
              <strong>Acceso:</strong> conocer qué datos personales tenemos sobre él.
            </li>
            <li>
              <strong>Rectificación:</strong> corregir información inexacta o incompleta.
            </li>
            <li>
              <strong>Cancelación:</strong> solicitar la supresión de sus datos.
            </li>
            <li>
              <strong>Oposición:</strong> oponerse al tratamiento para finalidades específicas.
            </li>
            <li>
              <strong>Portabilidad:</strong> obtener una copia de sus datos en formato estructurado.
            </li>
            <li>
              <strong>Retirada del consentimiento:</strong> revocar consentimientos otorgados.
            </li>
            <li>
              <strong>Limitación:</strong> restringir el tratamiento en supuestos específicos.
            </li>
          </ul>
          <p className="mt-3">
            El ejercicio de estos derechos puede solicitarse a{" "}
            <a href="mailto:privacidad@dealider.com" className="text-indigo-600 hover:underline">
              privacidad@dealider.com
            </a>
            . Dealider responderá en un plazo máximo de <strong>20 días hábiles</strong> y, en su
            caso, ejecutará la solicitud dentro de los <strong>15 días hábiles</strong> siguientes.
          </p>
        </Section>

        <Section n="VIII" title="Uso de Tecnologías de Rastreo (Cookies, SDKs)">
          <p>
            La Plataforma utiliza cookies, SDKs y tecnologías similares para autenticación,
            preferencias del Usuario, métricas de uso y diagnóstico técnico. El Usuario puede
            configurar su navegador o dispositivo para limitar el uso de cookies, entendiendo que
            ello puede afectar funcionalidades de la Plataforma.
          </p>
        </Section>

        <Section n="IX" title="Período de Conservación">
          <ul className="list-disc pl-6 space-y-1">
            <li>
              <strong>Cuenta de Usuario:</strong> durante la vigencia del contrato y hasta cinco
              (5) años posteriores a su terminación.
            </li>
            <li>
              <strong>Datos de Clientes/Prospectos:</strong> hasta 30 días naturales como período
              de gracia tras la cancelación, salvo solicitud expresa de eliminación inmediata.
            </li>
            <li>
              <strong>Logs y registros técnicos:</strong> entre 6 meses y 2 años, según el tipo
              de registro.
            </li>
          </ul>
        </Section>

        <Section n="X" title="Modificaciones al Aviso de Privacidad">
          <p>
            Dealider podrá actualizar este Aviso. Las modificaciones serán notificadas con al
            menos <strong>30 días naturales</strong> de anticipación a través de la Plataforma o
            por correo electrónico.
          </p>
        </Section>

        <Section n="XI" title="Menores de Edad">
          <p>
            La Plataforma está dirigida exclusivamente a personas <strong>mayores de 18 años</strong>.
            Dealider no recaba conscientemente datos personales de menores de edad. Si se detecta
            el registro de un menor, se procederá a eliminar la cuenta correspondiente.
          </p>
        </Section>

        <Section n="XII" title="Cambios en la Estructura Corporativa">
          <p>
            En caso de fusiones, adquisiciones, escisiones o cualquier otro cambio en la
            estructura corporativa de Dragon Technologies SAPI de CV, los datos personales podrán
            transferirse a la entidad sucesora, manteniéndose las mismas finalidades y medidas de
            protección establecidas en este Aviso.
          </p>
        </Section>

        <Section n="XIII" title="Contacto para Asuntos de Privacidad">
          <p>
            Para cualquier asunto relacionado con la privacidad y protección de datos personales,
            el titular puede contactarnos en:
          </p>
          <p className="mt-2">
            <strong>Email:</strong>{" "}
            <a href="mailto:privacidad@dealider.com" className="text-indigo-600 hover:underline">
              privacidad@dealider.com
            </a>
            <br />
            <strong>Soporte:</strong>{" "}
            <a href="mailto:soporte@dealider.com" className="text-indigo-600 hover:underline">
              soporte@dealider.com
            </a>
            <br />
            <strong>Teléfono:</strong> +52 1 55 3792 5358
            <br />
            <strong>Domicilio:</strong> Río Nazas 42, Cuauhtémoc, Ciudad de México, CP 06500
          </p>
        </Section>

        <div className="mt-10 p-4 rounded-lg bg-zinc-50 border border-zinc-200 text-sm text-zinc-700">
          <strong>ACEPTACIÓN:</strong> Al registrarse, iniciar sesión o utilizar de cualquier forma
          la Plataforma Dealider, usted declara haber leído, comprendido y aceptado íntegramente
          este Aviso de Privacidad.
        </div>

        <footer className="mt-16 pt-6 border-t border-zinc-200 text-xs text-zinc-500 text-center">
          Última actualización: 21 de mayo de 2026 · Dealider
        </footer>
      </main>
    </div>
  );
}

function Section({ n, title, children }) {
  return (
    <section className="mb-8">
      <h2 className="text-xl sm:text-2xl font-semibold text-zinc-900 mb-3">
        {n}. {title}
      </h2>
      <div className="text-zinc-700 space-y-3">{children}</div>
    </section>
  );
}
